// ./src/components/features/EventVendors/vendorHistoryWords.ts
//
// ROADMAP #202 — how the record of a request READS, on screen and in the download, so the two say the same words.
// ⚠ Pure, on purpose: `check-vendor-history` runs it, and the `@/utils` and `@/utils/api` barrels cannot load outside a browser build.
import dayjs from "dayjs";

import { formatDateTime } from "@em/utils";

import type { EventVendorHistory } from "@/utils/api/EventVendorService";
import { VENDOR_HISTORY_ACTOR, VENDOR_HISTORY_KIND } from "@/utils/api/vendorHistoryVocabulary";
import { buildCsv, csvText } from "@/utils/csvHelpers";

// The request's own states: the server's words (the API's `check-vendor-compliance` compares them).
const REQUEST_APPROVED = "approved";
const REQUEST_TO_REVIEW = "submitted";
const REQUEST_STATUS_WORDS: Record<string, string> = { missing: "Not received", [REQUEST_TO_REVIEW]: "To review", [REQUEST_APPROVED]: "Approved", returned: "Sent back" };

export const VENDOR_HISTORY_LABEL: Record<string, string> = {
  [VENDOR_HISTORY_KIND.APPROVED]: "Approved",
  [VENDOR_HISTORY_KIND.RETURNED]: "Sent back",
  [VENDOR_HISTORY_KIND.DOCUMENT_RECEIVED]: "Document received",
  [VENDOR_HISTORY_KIND.DOCUMENT_REMOVED]: "Document taken off the vendor's file",
  [VENDOR_HISTORY_KIND.TYPE_CHANGED]: "Another type of document asked for",
  [VENDOR_HISTORY_KIND.CHECKS_CHANGED]: "What it must show was changed",
  [VENDOR_HISTORY_KIND.KIND_CHANGED]: "Another kind of document asked for",
  [VENDOR_HISTORY_KIND.DOCUMENT_CHANGED]: "The document's details were corrected",
  [VENDOR_HISTORY_KIND.REQUEST_REMOVED]: "Stopped asking for it",
};

/** Status colours are status, never brand: the decisions as the request's own badges show them, the rest neutral. */
export const VENDOR_HISTORY_COLOR: Record<string, string> = {
  [VENDOR_HISTORY_KIND.APPROVED]: "green",
  [VENDOR_HISTORY_KIND.RETURNED]: "orange",
  [VENDOR_HISTORY_KIND.DOCUMENT_RECEIVED]: "blue",
};

/** The two that are decisions: what a filter for just those asks the server for. */
export const VENDOR_HISTORY_DECISIONS: string[] = [VENDOR_HISTORY_KIND.APPROVED, VENDOR_HISTORY_KIND.RETURNED];

export const vendorHistoryLabel = (line: EventVendorHistory): string => VENDOR_HISTORY_LABEL[line.kind ?? ""] ?? "Changed";

/** Who: the person, as recorded. ⓘ A vendor's link needs no account, so it says the link, never a person. */
export const vendorHistoryWho = (line: EventVendorHistory): string => {
  if (line.actorKind === VENDOR_HISTORY_ACTOR.VENDOR) {
    return "The vendor, through their link";
  }

  if (line.actorKind === VENDOR_HISTORY_ACTOR.UNKNOWN) {
    return "Not recorded";
  }

  return line.actorName || (line.actorKind === VENDOR_HISTORY_ACTOR.VENUE ? "The venue" : "Your team");
};

/** A check taken off (how one is waived) and one added: the two lists told apart. ⚠ No earlier list is no change, not an empty list that everything was added to. */
export const vendorHistoryChecksChange = (line: EventVendorHistory): { off: string[]; added: string[] } => {
  if (!line.previousChecks) {
    return { off: [], added: [] };
  }

  const before = line.previousChecks;
  const now = line.checks ?? [];

  return { off: before.filter((c) => !now.includes(c)), added: now.filter((c) => !before.includes(c)) };
};

const CHANGE_LABEL: Record<string, string> = {
  expiresOn: "Expiry",
  issuedOn: "Issue date",
  referenceNo: "Number",
  documentKind: "Kind",
  documentType: "Type",
};
const DAY_FIELDS = ["expiresOn", "issuedOn"];

/** "Expiry: Mar 31, 2027 → Jun 1, 2027". A value cleared or newly given says so. */
export const vendorHistoryChangeLines = (line: EventVendorHistory): string[] =>
  (line.changes ?? []).map((change) => {
    const field = change.field ?? "";
    const value = (v?: string | null): string => (!v ? "none" : DAY_FIELDS.includes(field) ? dayjs(v).format("MMM D, YYYY") : v);

    return `${CHANGE_LABEL[field] ?? "Changed"}: ${value(change.from)} → ${value(change.to)}`;
  });

const isDecision = (line: EventVendorHistory): boolean => VENDOR_HISTORY_DECISIONS.includes(line.kind ?? "");

/** The checks a decision did not tick. */
export const vendorHistoryUnticked = (line: EventVendorHistory): string[] => (isDecision(line) ? (line.checks ?? []).filter((c) => !(line.checksMet ?? []).includes(c)) : []);

/** It was approved, and this took that back. */
export const vendorHistoryBackToReview = (line: EventVendorHistory): boolean =>
  line.previousReviewStatus === REQUEST_APPROVED && line.reviewStatus === REQUEST_TO_REVIEW;

/**
 * The record as a spreadsheet. ⚠ The note, the names and the checks are typed by people outside the team (a venue's representative, a
 * vendor's name): `csvText`, or a value starting with `=` runs as a formula when the file is opened.
 */
export const vendorHistoryCsv = (lines: EventVendorHistory[]): string => {
  const text = (values: string[] | null | undefined): string => csvText((values ?? []).join("; "));
  const rows = lines.map((line) => [
    line.happenedAt ? formatDateTime(line.happenedAt, "datetime") : "",
    line.happenedAt ?? "",
    csvText(line.vendorName),
    csvText(line.documentName),
    vendorHistoryLabel(line),
    REQUEST_STATUS_WORDS[line.previousReviewStatus ?? ""] ?? "",
    REQUEST_STATUS_WORDS[line.reviewStatus ?? ""] ?? "",
    csvText(vendorHistoryWho(line)),
    csvText(line.note),
    text(line.checks),
    isDecision(line) ? text(line.checksMet) : "",
    text(vendorHistoryChecksChange(line).off),
    text(vendorHistoryChecksChange(line).added),
    csvText(vendorHistoryChangeLines(line).join("; ")),
    line.documentExpiresOn ?? "",
    line.documentRemoved ? "Yes" : "",
    line.recordedLater ? "Yes" : "",
  ]);

  return buildCsv(["When", "When (UTC)", "Vendor", "Document", "What happened", "Status before", "Status after", "Who", "Note", "It had to show", "Ticked", "Check taken off", "Check added", "Other changes", "Document expires", "Document since taken off the file", "Copied in later"], rows);
};
